import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { formatCAD, formatDate, ROLE_LABEL } from "@/lib/utils-cart";
import { GatewayBadge, PaymentStatusBadge, RoleBadge } from "@/components/Badges";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Area, AreaChart, CartesianGrid } from "recharts";
import { ArrowUpRight, Sparkles, Package, ShoppingCart, Wallet, Users, Store, Briefcase, Megaphone } from "lucide-react";

export default function Dashboard() {
  const { user, primaryRole } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/orders/mine").then((r) => setOrders(r.data.slice(0, 8))).catch(() => {});
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
  }, []);

  const role = primaryRole();

  return (
    <DashboardLayout
      title={`Welcome back, ${user?.full_name?.split(" ")[0] || "operator"}`}
      subtitle="Your command center across sourcing, selling, shipping, and growth."
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Revenue" value={formatCAD(analytics?.revenue || 0)} icon={Wallet} accent="text-secondary" />
        <Stat label="Orders" value={analytics?.orders || 0} icon={ShoppingCart} accent="text-primary" />
        <Stat label="AOV" value={formatCAD(analytics?.aov || 0)} icon={ArrowUpRight} accent="text-accent-blue" />
        <Stat label="Customers" value={analytics?.customers || 0} icon={Users} accent="text-accent-green" />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 v-card">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="label-eyebrow text-secondary">Revenue</div>
              <h3 className="font-display text-xl font-semibold text-primary mt-1">Last 14 days</h3>
            </div>
            <RoleBadge role={role} />
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics?.series || []} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--secondary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--secondary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "white", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--secondary))" fill="url(#rev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="v-card">
          <div className="label-eyebrow text-secondary">Wallet</div>
          <div className="mt-3">
            <div className="text-xs text-muted-foreground">Available balance</div>
            <div className="font-display font-black text-3xl text-primary mt-1">{formatCAD(wallet?.balance || 0)}</div>
            <div className="mt-1 text-xs text-muted-foreground font-mono">+ {formatCAD(wallet?.pending || 0)} pending</div>
          </div>
          <div className="mt-6 pt-4 border-t border-border">
            <Link to="/app/wallet" className="v-btn-outline w-full" data-testid="dash-wallet-link">Manage wallet & payouts</Link>
          </div>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 v-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-primary">Recent orders</h3>
            <Link to="/app/orders" className="text-sm text-secondary font-medium" data-testid="dash-orders-link">View all <ArrowUpRight size={14} className="inline" /></Link>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2">Order</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Gateway</th>
                  <th className="py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                    <td className="py-3">
                      <div className="font-mono text-xs">{o.order_number}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(o.created_at)}</div>
                    </td>
                    <td><PaymentStatusBadge status={o.payment_status} /></td>
                    <td><GatewayBadge gateway={o.gateway} /></td>
                    <td className="text-right font-mono">{formatCAD(o.total)}</td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="v-card">
          <h3 className="font-display text-lg font-semibold text-primary">Quick actions</h3>
          <div className="mt-4 grid gap-2">
            <QuickAction to="/app/marketing/ai" icon={Sparkles} label="Generate AI marketing" />
            <QuickAction to="/app/products" icon={Package} label="Manage products" />
            <QuickAction to="/app/store" icon={Store} label="Edit storefront" />
            <QuickAction to="/app/crm" icon={Users} label="View customers" />
            <QuickAction to="/app/leads" icon={Megaphone} label="Browse leads" />
          </div>
        </div>
      </div>

      {analytics?.top_products?.length > 0 && (
        <div className="mt-6 v-card">
          <h3 className="font-display text-lg font-semibold text-primary">Top products by revenue</h3>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            {analytics.top_products.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <span className="font-display font-black text-2xl text-muted-foreground w-8">{i + 1}</span>
                <Link to={`/product/${p.id}`} className="flex-1 text-primary font-medium hover:underline">{p.title}</Link>
                <span className="font-mono">{formatCAD(p.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="v-card">
      <Icon size={20} className={accent} strokeWidth={1.75} />
      <div className="mt-6">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display font-black text-2xl text-foreground mt-1">{value}</div>
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-3 rounded-sm border border-border hover:border-primary/30 hover:bg-muted transition-colors text-sm" data-testid={`quick-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={16} className="text-secondary" />
      <span className="flex-1 text-foreground">{label}</span>
      <ArrowUpRight size={14} className="text-muted-foreground" />
    </Link>
  );
}
